import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { downloadInvoicePdf, getInvoice } from "../lib/api";
import { formatEUR, formatCurrency, formatPeriod, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, FileDown, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import GestionSyncDialog from "../components/GestionSyncDialog";

const MONTHS_EN = ["", "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getInvoice(id);
        setInv(data);
      } catch {
        toast.error("Factura no encontrada");
        nav("/");
      }
    })();
  }, [id, nav]);

  if (!inv) return <div className="text-slate-400">Cargando...</div>;

  const currency = inv.currency || "EUR";
  const exchangeRate = Number(inv.exchange_rate || 0);
  const subtotal = inv.lines.reduce((a, l) => a + (l.amount || 0), 0);
  const vat = subtotal * (inv.vat_rate / 100);
  const total = subtotal + vat;
  const totalEur = currency !== "EUR" && exchangeRate > 0 ? total / exchangeRate : total;

  const grouped = { services: [], trips: [], extras: [] };
  inv.lines.forEach((l) => { (grouped[l.group] || grouped.services).push(l); });

  const servicesBanner = `SERVICES ${MONTHS_EN[inv.period_month]} ${inv.period_year}`;

  return (
    <div data-testid="invoice-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft size={14} /> Volver al panel
        </button>
        <div className="flex gap-2">
          {inv.type !== "draft-only" && (inv.trips?.length || 0) > 0 && (
            <Button variant="outline" onClick={() => setSyncOpen(true)} data-testid="sync-gestion-btn">
              <RefreshCw size={14} className="mr-2" /> Sincronizar con Gestor
            </Button>
          )}
          <Button variant="outline" onClick={() => nav(`/factura/${id}/editar`)} data-testid="edit-btn">
            <Pencil size={14} className="mr-2" /> Editar
          </Button>
          <Button onClick={() => downloadInvoicePdf(inv.id, inv.number)} className="bg-slate-900 hover:bg-slate-800" data-testid="download-pdf-btn">
            <FileDown size={14} className="mr-2" /> Descargar PDF
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-8 md:p-12 max-w-4xl mx-auto shadow-sm">
        {/* Header */}
        <div className="flex justify-between items-start border-b border-slate-200 pb-6">
          <div>
            <h1 className="font-cabinet text-4xl font-bold tracking-tighter text-slate-900">FACTURA</h1>
            <div className="mt-4 space-x-1">
              <Badge variant="outline" className={inv.type === "advance" ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-emerald-50 text-emerald-700 border-emerald-100"}>
                {inv.type === "advance" ? "Adelanto" : "Final"}
              </Badge>
              <Badge variant="outline" className={inv.status === "issued" ? "bg-slate-900 text-white border-slate-900" : "bg-amber-50 text-amber-700 border-amber-100"}>
                {inv.status === "issued" ? "Emitida" : "Borrador"}
              </Badge>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Nº Factura</div>
            <div className="font-cabinet text-2xl font-bold text-slate-900">{inv.number}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-3">Fecha emisión</div>
            <div className="text-slate-900 font-medium">{formatDate(inv.issue_date)}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-3">Periodo</div>
            <div className="text-slate-900 font-medium">{formatPeriod(inv.period_month, inv.period_year)}</div>
          </div>
        </div>

        {/* Parties */}
        <div className="grid md:grid-cols-2 gap-8 py-6 border-b border-slate-200">
          <PartyBlock label="EMISOR" p={inv.issuer} />
          <PartyBlock label="CLIENTE" p={inv.recipient} />
        </div>

        {/* Lines table */}
        <div className="py-6">
          <div className="border border-slate-300 rounded-md overflow-hidden">
            {/* SERVICES banner */}
            {grouped.services.length > 0 && (
              <>
                <div className="bg-[#d9e8c1] text-center font-bold py-2.5 text-slate-900 border-b border-slate-300">
                  {servicesBanner}
                </div>
                {grouped.services.map((l) => (
                  <LineItemRow key={l.id} description={l.description} amount={l.amount} currency={currency} />
                ))}
              </>
            )}

            {/* Trips: render headers and data items */}
            {grouped.trips.map((l) => (
              l.is_header ? (
                <div key={l.id} className="text-center font-bold py-2.5 text-slate-900 border-t border-b border-slate-300 bg-slate-50">
                  {l.description}
                </div>
              ) : (
                <LineItemRow key={l.id} description={l.description} amount={l.amount} currency={currency} />
              )
            ))}

            {/* EXTRAS banner */}
            {grouped.extras.length > 0 && (
              <>
                <div className="text-center font-bold py-2.5 text-slate-900 border-t border-b border-slate-300 bg-slate-50">
                  EXTRAS
                </div>
                {grouped.extras.map((l) => (
                  <LineItemRow key={l.id} description={l.description} amount={l.amount} currency={currency} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end pt-2 border-t border-slate-200">
          <div className="w-80 space-y-2 text-sm pt-4">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono tabular-nums">{formatCurrency(subtotal, currency)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">IVA ({inv.vat_rate}%)</span><span className="font-mono tabular-nums">{formatCurrency(vat, currency)}</span></div>
            <div className="flex justify-between items-baseline border-t-2 border-slate-900 pt-2 mt-2">
              <span className="font-bold text-slate-900">TOTAL</span>
              <span className="font-cabinet text-2xl font-bold text-slate-900 tabular-nums" data-testid="invoice-total">{formatCurrency(total, currency)}</span>
            </div>
            {currency !== "EUR" && exchangeRate > 0 && (
              <>
                <div className="flex justify-between pt-2 text-xs text-slate-500">
                  <span>Tipo de cambio</span>
                  <span className="font-mono">1 € = {exchangeRate.toFixed(4)} {currency}</span>
                </div>
                <div className="flex justify-between items-baseline border-t-2 border-slate-900 pt-2 mt-1">
                  <span className="font-bold text-slate-900">TOTAL EUR</span>
                  <span className="font-cabinet text-2xl font-bold text-slate-900 tabular-nums" data-testid="invoice-total-eur">{formatEUR(totalEur)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {inv.notes && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Notas</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-8 leading-snug">
          En cumplimiento de la Ley Orgánica 3/2018 de Protección de Datos y Derechos Digitales y el Reglamento Europeo de Protección de Datos 2016/679, le recordamos que sus datos son tratados por VIAJA DE VERDAD S.L.. Puede ejercitar gratuitamente los derechos de acceso, oposición, rectificación, cancelación o supresión, revocación del consentimiento, portabilidad y limitación del tratamiento de los datos, dirigiéndose a VIAJA DE VERDAD S.L., Avda. Miguel Delibes No 32, Bajo, Madrid, C.P. 28052.
        </p>
      </div>

      <GestionSyncDialog
        open={syncOpen}
        onOpenChange={setSyncOpen}
        invoiceId={inv.id}
        invoiceNumber={inv.number}
        invoiceType={inv.type}
        trips={inv.trips || []}
      />
    </div>
  );
}

function LineItemRow({ description, amount, currency = "EUR" }) {
  return (
    <div className="flex border-b border-slate-300 last:border-b-0">
      <div className="flex-1 px-4 py-3 text-sm text-slate-700 border-r border-slate-300">{description}</div>
      <div className="w-36 px-4 py-3 text-right font-mono tabular-nums text-sm text-slate-900">{formatCurrency(amount, currency)}</div>
    </div>
  );
}

function PartyBlock({ label, p }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</div>
      <div className="text-sm text-slate-700 space-y-0.5">
        <div className="font-semibold text-slate-900 text-base">{p.name}</div>
        {p.tax_id && <div>NIF / Tax ID: {p.tax_id}</div>}
        {p.address_line1 && <div>{p.address_line1}</div>}
        {p.address_line2 && <div>{p.address_line2}</div>}
        {(p.postal_code || p.city) && <div>{p.postal_code} {p.city}</div>}
        {p.country && <div>{p.country}</div>}
        {p.phone && <div>Tel: {p.phone}</div>}
        {p.email && <div>{p.email}</div>}
        {p.contact_name && <div>Contacto: {p.contact_name}</div>}
      </div>
    </div>
  );
}
